import { useCallback } from 'react';
import { AiConversation, AiScopeTarget } from '@shared/models/ai-chat.model';
import type { OpenAiAllowedModels } from '@shared/models/ai/ai-models.model';
import { useAiChatStore, useOrgChatState } from './ai-chat.store';

export type AiChatPending = { status: 'pending' };
export type AiChatError = {
  status: 'error';
  error: Error;
  refetch: () => Promise<void>;
};
export type AiChatReady = {
  status: 'ready';
  conversation: AiConversation;
  isFetching: boolean;
  isSending: boolean;
  sendError: Error | null;
  refetch: () => Promise<void>;
  sendMessage: (
    text: string,
    scopeTarget: AiScopeTarget,
    model?: OpenAiAllowedModels,
    files?: File[],
  ) => Promise<void>;
  createNewConversation: () => Promise<void>;
  deleteConversation: () => Promise<void>;
  cancelPendingMessages: (messageId?: string) => Promise<void>;
  switchConversation: (conversation: AiConversation) => void;
};

export type UseAiChatReturn = AiChatPending | AiChatError | AiChatReady;

/**
 * AI chat for the current user + organization + project bucket.
 *
 * Each `(organization, projectId)` pair has its own live conversation slice.
 * `projectId === null` means organization-level chat (user not inside any
 * project). Per-turn scope (design / project / elements / organization) lives
 * on each {@link AiMessage} via `sendMessage`'s `scopeTarget` arg, so the
 * chat itself doesn't need to be re-mounted when the user switches view
 * within the same project.
 *
 * State lives in the shared zustand store keyed by `(organization, projectId)`,
 * so multiple consumers for the same bucket stay in sync. The store also
 * receives real-time socket updates via `upsertConversation`.
 */
export const useAiChat = (
  organization: string,
  projectId: number | null,
): UseAiChatReturn => {
  const state = useOrgChatState(organization, projectId);

  const refetch = useCallback(
    () => useAiChatStore.getState().fetchFor(organization, projectId),
    [organization, projectId],
  );

  const sendMessage = useCallback(
    (
      text: string,
      scopeTarget: AiScopeTarget,
      model?: OpenAiAllowedModels,
      files?: File[],
    ) =>
      useAiChatStore
        .getState()
        .sendMessage(organization, projectId, text, scopeTarget, model, files),
    [organization, projectId],
  );

  const createNewConversation = useCallback(
    () =>
      useAiChatStore.getState().createNewConversation(organization, projectId),
    [organization, projectId],
  );

  const deleteConversation = useCallback(
    () => useAiChatStore.getState().deleteConversation(organization, projectId),
    [organization, projectId],
  );

  const cancelPendingMessages = useCallback(
    (messageId?: string) =>
      useAiChatStore
        .getState()
        .cancelPendingMessages(organization, projectId, messageId),
    [organization, projectId],
  );

  const switchConversation = useCallback(
    (conversation: AiConversation) =>
      useAiChatStore
        .getState()
        .setConversation(organization, projectId, conversation),
    [organization, projectId],
  );

  switch (state.status) {
    case 'idle':
    case 'fetching':
      return { status: 'pending' } as const;
    case 'error':
      return { status: 'error', error: state.error, refetch } as const;
    case 'ready':
      return {
        status: 'ready',
        conversation: state.conversation,
        isFetching: state.isFetching,
        isSending: state.isSending,
        sendError: state.sendError,
        refetch,
        sendMessage,
        createNewConversation,
        deleteConversation,
        cancelPendingMessages,
        switchConversation,
      } as const;
  }
};
